import React, { useEffect, useRef, useState, Fragment } from 'react';

/**
 * Reusable Tabs component
 */
const Tabs = ({ tabs, defaultTab, activeTab: controlledActiveTab, onTabChange, className = '' }) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);
  const tabRefs = useRef({});
  
  // Use controlled activeTab if provided, otherwise use internal state
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  useEffect(() => {
    const activeButton = tabRefs.current[activeTab];
    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [activeTab]);

  const handleTabClick = (tabId) => {
    setInternalActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className={`tabs-container ${className}`}>
      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <nav
          className="-mb-px flex flex-nowrap gap-4 sm:gap-8 overflow-x-auto overscroll-x-contain"
          role="tablist"
          aria-label="Page tabs"
        >
          {tabs.map((tab) => (
            <Fragment key={tab.id}>
              {tab.leadingSeparator && (
                <span
                  className="shrink-0 self-center py-2 px-1 sm:px-2 text-text-muted text-sm font-light select-none pointer-events-none"
                  aria-hidden="true"
                >
                  |
                </span>
              )}
              <button
                ref={(node) => {
                  if (node) tabRefs.current[tab.id] = node;
                }}
                onClick={() => handleTabClick(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`
                py-2 px-1 border-b-2 font-medium text-sm transition-colors shrink-0 whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
                }
              `}
              >
                <div className="flex items-center space-x-2">
                  {tab.icon && <span className="text-lg">{tab.icon}</span>}
                  {tab.mobileLabel ? (
                    <>
                      <span className="sm:hidden">{tab.mobileLabel}</span>
                      <span className="hidden sm:inline">{tab.label}</span>
                    </>
                  ) : (
                    <span>{tab.label}</span>
                  )}
                  {(tab.badge !== undefined && tab.badge !== null) && (
                    <span className="ml-2 bg-surface-subtle text-text-secondary text-xs px-2 py-1 rounded-full">
                      ({tab.badge})
                    </span>
                  )}
                </div>
              </button>
            </Fragment>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTabContent}
      </div>
    </div>
  );
};

export default Tabs;
